import axios, { AxiosError, AxiosResponse } from "axios";
import { readToken } from "../services/localStorage.Service";
import {
  AdresseBody,
  AdresseResponse,
  AffecterProjectBody,
  Commune,
  Prefecture,
  ProjectBody,
  ProjectDetailsModel,
  ProjectInterface,
  ProjectModel,
  ProjectNamesModel,
  Region,
} from "../domaine/projectModel";
import toast from "react-hot-toast";
import { Modal } from "antd";

const apiUrl = process.env.REACT_APP_API_URL;

export const axiosInstance = axios.create({
  baseURL: apiUrl,
});

axiosInstance.interceptors.request.use((config: any) => {
  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${readToken()}`,
  };
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error(
        "HTTP error response:",
        error.response.data || "No response data available"
      );
      return Promise.reject(error.response.data);
    } else if (error.request) {
      // The request was made but no response was received
      console.error("HTTP no response:", error.request);
      return Promise.reject({
        message: "No response received from the server",
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error("HTTP request setup error:", error.message);
      return Promise.reject({ message: "Error setting up the request" });
    }
  }
);
export const fetchProjectsApi = async (
  apiUrl: string
): Promise<ProjectInterface[]> => {
  try {
    const response: AxiosResponse<ProjectInterface[]> =
      await axiosInstance.get<any>(apiUrl);
    return response.data;
  } catch (error: any) {
    console.error("Fetch projects failed", error);
    throw error.response?.data || "Fetch projects failed";
  }
};
export const deleteProjectApi = async (
  ProjectId: number,
  apiUrl: string
): Promise<void> => {
  try {
    await axiosInstance.delete<void>(`${apiUrl}${ProjectId}/`);
  } catch (error: any) {
    console.error("Delete project failed", error);
    throw error.response?.data || "Delete project failed";
  }
};


export const deleteTrancheApi = async (
  trancheId: number,
  apiUrl: string
): Promise<void> => {
  try {
    await axiosInstance.delete<void>(`${apiUrl}${trancheId}/`);
  } catch (error: any) {
    console.error("Delete tranche failed", error);
    throw error.response?.data || "Delete tranche failed";
  }
};

export const fetchProjectDetailsApi = async (
  ProjectId: number,
  apiUrl: string
): Promise<ProjectDetailsModel> => {
  try {
    const response: AxiosResponse<ProjectDetailsModel> =
      await axiosInstance.get<ProjectDetailsModel>(`${apiUrl}${ProjectId}/`);
    return response.data;
  } catch (error: any) {
    console.error("fetch project details failed", error);
    throw error.response?.data || "fetch project details failed";
  }
};

export const fetchProjectsNamesApi = async (
  apiUrl: string
): Promise<ProjectNamesModel[]> => {
  try {
    const response: AxiosResponse<ProjectNamesModel[]> =
      await axiosInstance.get<ProjectNamesModel[]>(apiUrl);
    return response.data;
  } catch (error: any) {
    console.error("Fetch projects names failed", error);
    throw error.response?.data || "Fetch projects names failed";
  }
};

export const fetchProjectsByUserApi = async (
  apiUrl: string
): Promise<ProjectModel[]> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(apiUrl);
    return response.data.data;
  } catch (error: any) {
    console.error("Fetch projects by user failed", error);
    throw error.response?.data || "Fetch projects by user failed";
  }
};

export const fetchRegionsApi = async (apiUrl: string): Promise<Region[]> => {
  try {
    const response: AxiosResponse<Region[]> = await axiosInstance.get<Region[]>(
      apiUrl
    );
    return response.data;
  } catch (error: any) {
    console.error("Fetch regions failed", error);
    throw error.response?.data || "Fetch regions failed";
  }
};

export const fetchPrefecturesApi = async (
  regionId: number,
  apiUrl: string
): Promise<Prefecture[]> => {
  try {
    const response: AxiosResponse<Prefecture[]> = await axiosInstance.get<
      Prefecture[]
    >(`${apiUrl}${regionId}/`);
    return response.data;
  } catch (error: any) {
    console.error("Fetch prefectures failed", error);
    throw error.response?.data || "Fetch prefectures failed";
  }
};

export const fetchCommunesApi = async (
  prefectureId: number,
  apiUrl: string
): Promise<Commune[]> => {
  try {
    const response: AxiosResponse<Commune[]> = await axiosInstance.get<
      Commune[]
    >(`${apiUrl}${prefectureId}/`);
    return response.data;
  } catch (error: any) {
    console.error("Fetch commune failed", error);
    throw error.response?.data || "Fetch commune failed";
  }
};

export const addAdresseApi = async (
  newAdress: AdresseBody,
  apiUrl: string
): Promise<number> => {
  try {
    const response: AxiosResponse<AdresseResponse> =
      await axiosInstance.post<AdresseResponse>(apiUrl, newAdress);
    return response.data.data.id;
  } catch (error: any) {
    console.error("Add adresse failed", error);
    throw error.response?.data || "Add adresse failed";
  }
};

export const addProjectApi = async (
  newProject: ProjectBody,
  apiUrl: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      apiUrl,
      newProject
    );
    return response.data;
  } catch (error: any) {
    console.error("Add project failed", error);
    throw error.response?.data || "Add project failed";
  }
};

export const addTrancheApi = async (
  newTranche: any,
  apiUrl: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      apiUrl,
      newTranche
    );
    return response.data;
  } catch (error: any) {
    console.error("Add Tranche failed", error);
    throw error.response?.data || "Add Tranche failed";
  }
};

export const affecterProjectApi = async (
  body: AffecterProjectBody,
  apiUrl: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.post<any>(
      apiUrl,
      body
    );
    if (response.data.errors) {
      toast.error("Certaines affectations ont échoué.");
  
      const errorMessages = response.data.errors.map((error: any) => `${error}`).join('\n');
  
      // Display the error messages in an Ant Design modal
      Modal.error({
        title: 'Erreurs',
        content: errorMessages,
        okText: 'Fermer',
        centered:true,
        okButtonProps: {
          type: 'primary'
        },
      });
    }
    return response.data;
  } catch (error: any) {
    if (error.error === "Cette liaison existe déjà.") {
      toast.error("Un ou plusieurs projets sont déjà affectés à ce visiteur.");
    } else if (error.errors) {
      // Handle the array of errors we're now returning from the backend
      toast.error("Certaines affectations ont échoué.");
      const errorMessages = error.errors.map((error: any) => `${error}`).join('\n');
  
      // Display the error messages in an Ant Design modal
      Modal.error({
        title: 'Erreurs',
        content: errorMessages,
        okText: 'Fermer',
        centered:true,
        okButtonProps: {
          type: 'primary'
        },
      });
      console.error("Errors:", error.errors);
    } else {
      toast.error("Une erreur s'est produite.");
    }
    console.error("Affecter project failed", error);
    throw error.response?.data || "Affecter project failed";
  }
};




export const retirerProjectApi = async (
  idProjet: number,
  idUser: number,
  apiUrl: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.delete<any>(
      apiUrl+idProjet+"/"+idUser+"/unassign/"
    );
    return response.data;
  } catch (error: any) {
    console.error("Retirer liaison failed", error);
    throw error.response?.data || "Retirer liaison failed";
  }
};

export const completeProjectApi = async (
  idProjet: number,
  idUser: number,
  apiUrl: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.put<any>(
      apiUrl+idProjet+"/"+idUser+"/complete/"
    );
    return response.data;
  } catch (error: any) {
    console.error("Complete liaison failed", error);
    throw error.response?.data || "Complete liaison failed";
  }
};

export const editProjectApi = async (
  newProject: any,
  ProjectId: number,
  apiUrl: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${apiUrl}${ProjectId}/`,
      newProject
    );
    return response.data;
  } catch (error: any) {
    console.error("Edit project failed", error);
    throw error.response?.data || "Edit project failed";
  }
};



export const editTrancheApi = async (
  newTranche: any,
  trancheId: number,
  apiUrl: string
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.patch<any>(
      `${apiUrl}${trancheId}/`,
      newTranche
    );
    return response.data;
  } catch (error: any) {
    console.error("Edit tranche failed", error);
    throw error.response?.data || "Edit tranche failed";
  }
};

export const fetchAdressefromOpenStreetMapApi = async (
  latitude: number,
  longitude: number
): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get<any>(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Fetch adresse from openstreetmap failed", error);
    throw error.response?.data || "Fetch adresse from openstreetmap failed";
  }
};
